import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreferredTiming } from '@/types';

interface PreferredTimePickerProps {
  value: PreferredTiming;
  onChange: (timing: PreferredTiming) => void;
  disabled?: boolean;
}

// The three within-slot buckets, in slot order. Anytime (no preference) is the
// toggle-off state and never rendered here — this control only appears once the
// merchant has opted in, so it always shows a chosen Early/Mid/Late.
const TIMINGS: PreferredTiming[] = ['early', 'mid', 'late'];

// Preferred-timing primitive (spec v1.1): a 3-way Early / Mid / Late segmented
// choice WITHIN the picked slot. The merchant only ever sees the labels — the
// clock windows (thirds) are internal to fleet. Reuses SlotPicker/PreferredTime
// button vocabulary VERBATIM (1px→2px border on select with padding held
// constant so nothing shifts; the §2.5 selected tint) and the same WAI-ARIA
// radiogroup + roving-tabindex arrow-key semantics, so it feels identical to
// every other small control in the pickup feature.
const PreferredTimePicker = ({ value, onChange, disabled = false }: PreferredTimePickerProps) => {
  const { t } = useTranslation();

  const rovingTiming = TIMINGS.includes(value) ? value : TIMINGS[0];

  const btnRefs = useRef<Map<PreferredTiming, HTMLButtonElement>>(new Map());
  const registerRef = (timing: PreferredTiming) => (el: HTMLButtonElement | null) => {
    if (el) {
      btnRefs.current.set(timing, el);
    } else {
      btnRefs.current.delete(timing);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const currentIndex = TIMINGS.findIndex((timing) => timing === rovingTiming);
    let nextIndex: number;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % TIMINGS.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex =
          currentIndex === -1
            ? TIMINGS.length - 1
            : (currentIndex - 1 + TIMINGS.length) % TIMINGS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TIMINGS.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTiming = TIMINGS[nextIndex];
    onChange(nextTiming);
    btnRefs.current.get(nextTiming)?.focus();
  };

  return (
    <div
      role='radiogroup'
      aria-label={String(t('preferred_timing_label'))}
      className='grid grid-cols-3 gap-2'
      onKeyDown={handleKeyDown}
    >
      {TIMINGS.map((timing) => {
        const active = value === timing;
        return (
          <button
            key={timing}
            ref={registerRef(timing)}
            type='button'
            role='radio'
            aria-checked={active}
            disabled={disabled}
            tabIndex={timing === rovingTiming ? 0 : -1}
            onClick={() => onChange(timing)}
            className={[
              'flex items-center justify-center rounded-lg text-center text-sm font-bold transition-all',
              'active:scale-[0.98]',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(237,224,74)]',
              'disabled:cursor-not-allowed disabled:opacity-45',
              !active ? 'bg-white' : '',
              !active && !disabled ? 'hover:bg-[rgb(245,245,243)]' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            // Border WIDTH carries selection (1px → 2px) with padding held
            // constant (1px+9px = 2px+8px = 10px) so toggling never shifts size —
            // same no-layout-jank pattern as SlotPickerRow. Inline padding (not a
            // `p-*` utility) because this project's tailwind `important: true`
            // would let the utility beat the inline style.
            style={
              active
                ? {
                    border: '2px solid rgb(237,224,74)',
                    background: 'rgba(237,224,74,0.12)',
                    color: 'rgb(57,57,60)',
                    padding: '8px',
                  }
                : { border: '1px solid rgb(232,230,227)', padding: '9px' }
            }
          >
            {String(t(timing))}
          </button>
        );
      })}
    </div>
  );
};

export default PreferredTimePicker;
